import React from 'react';
import PropTypes from 'prop-types';
import Picture from '@deporvillage/picture-component';
import { formatPrice, Config, I18n } from '@deporvillage/utils-library-component';
import { ProductItemContext } from './ProductItemContext.js';
import { ProductItemType as Type } from './ProductItemType.js';

import styles from './ProductItem.module.scss';

const ProductItemClick = {
    Related: 'related',
};

const getImageSize = (type) => {
    switch (type) {
        case Type.Alternative:
            return 333;
        default:
            return 260;
    }
};

const ProductItem = ({ item, isSingleLine, isHovering, onClick }) => {

    const {
        title,
        isNew = item?.is_new,
        discountTag = item?.discount_tag,
        defaultVariant = item?.default_variant,
        isExclusive = item?.is_exclusive,
        isSustainable = item?.is_sustainable,
        isClearance = item?.is_clearance,
        gallery = item?.gallery_images,
        related = item?.related_products,
        url,
        isSponsored
    } = item;

    const { lang } = React.useContext(Config);
    const { type } = React.useContext(ProductItemContext);

    const [size] = React.useState(getImageSize(type));
    const [showRelated, setShowRelated] = React.useState(false);

    const { price, pvp, currency = defaultVariant.price?.currency_code } = defaultVariant.price;
    const discount = Math.round(100 * (pvp - price) / pvp);

    const tags = {
        isNew: isNew && !discountTag?.show,
        isClearance: isClearance && !isNew && discountTag?.show,
        isSustainable: isSustainable,
        isExclusive: isExclusive,
        hasDiscount: discountTag?.show,
        isSponsored: isSponsored,
    };

    const handleOnClickRelated = (e, redirectUrl) => {
        e.preventDefault();
        onClick && onClick(ProductItemClick.Related);
        /*eslint-disable-next-line no-undef*/
        window.location.href = redirectUrl || url;
    };

    React.useEffect(() => {
        if (isHovering) setShowRelated(isHovering);
    }, [isHovering]);

    return (
        <div
            className={`${styles['product-item-component']} ${styles[type || Type.Default]} ${isHovering ? styles[`hover`] : ``}`}
        >
            {
                isSponsored && (
                    <div className={styles['product-item-sponsored']}>
                        <span className={`${styles['tag-sponsored-icon']} icon icon-error-outline`}/>
                        <span className={styles['tag-sponsored']}><I18n>generic.list.item.sponsored</I18n></span>
                    </div>
                )
            }

            <div className={styles.background}>
                <div className={styles['product-item-picture-wrapper']}>
                    <Picture
                        src={'product/' + gallery.images[gallery.default]}
                        name={title}
                        width={size}
                        height={size}
                        lazy
                    />
                </div>
            </div>

            <div className={styles.description}>

                <div className={`${related && related.length ? styles['with-product-item-related'] : ''}`}>

                    <div className={styles['product-item-tags']}>
                        {tags.hasDiscount && <span className={styles['tag-discount']}>-{discount}%</span>}
                        {tags.isExclusive && !tags.isClearance && <span className={styles['tag-exclusive']}><I18n>generic.list.item.exclusive</I18n></span>}
                        {tags.isNew && <span className={styles['tag-new']}>New</span>}
                        {tags.isSustainable && !tags.isClearance && <span className={styles['tag-sustainable']}><I18n>generic.list.item.sustainable</I18n></span>}
                        {tags.isClearance && <span className={styles['tag-clearance']}><I18n>generic.list.item.clearance</I18n></span>}
                    </div>

                    {
                        related && related.length && showRelated ? (
                            <div className={styles['product-item-related']}>
                                <div className={styles['product-related-list']}>
                                    {related[0].items.slice(0, related[0].items.length > 5 ? 4 : 5).map((color, idx) => (
                                        <div
                                            data-testid={`color-link-${idx}`}
                                            key={color.url}
                                            title={color.name}
                                            className={styles['product-related-item']}
                                            onClick={(e) => handleOnClickRelated(e, color.url)}
                                        >
                                            <Picture
                                                src={`product/${color.image}`}
                                                name={color.name}
                                                width={36}
                                                height={36}
                                                resize
                                                lazy
                                            />
                                        </div>
                                    ))}
                                    {related.length && related[0].items.length > 5 ? (
                                        <div
                                            data-testid='color-link-more'
                                            className={styles['product-related-more']}
                                            title={title}
                                            onClick={handleOnClickRelated}
                                        >
                                            +{related[0].items.length - 4}{' '}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ) : null
                    }
                </div>

                <div className={`${styles['product-item-name']} ${isSingleLine ? styles['single-line'] : ``}`} dangerouslySetInnerHTML={{ __html: title }} />

                <div className={styles['product-item-price']}>
                    {discount && pvp ?
                        <span className={styles['price-special']}>
                            {formatPrice(price, currency, lang)}
                            <del className={styles['price-pvpr']}>{formatPrice(pvp, currency, lang)}</del>
                        </span> : <span>{formatPrice(price, currency, lang)}</span>
                    }
                </div>

            </div>

        </div>
    );
};

ProductItem.propTypes = {
    item: PropTypes.shape({
        title: PropTypes.string,
        gallery: PropTypes.shape({
            default: PropTypes.number,
            images: PropTypes.arrayOf(PropTypes.string)
        }),
        discounTag: PropTypes.shape({
            show: PropTypes.bool,
        }),
        defaultVariant: PropTypes.shape({
            price: PropTypes.shape({
                pvp: PropTypes.number,
                price: PropTypes.number,
                currency: PropTypes.string,
            }),
        }),
        isNew: PropTypes.bool,
        isExclusive: PropTypes.bool,
        isSustainable: PropTypes.bool,
        isClearance: PropTypes.bool,
        url: PropTypes.string,
        related: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                items: PropTypes.arrayOf(
                    PropTypes.shape({
                        url: PropTypes.string,
                        image: PropTypes.string,
                        name: PropTypes.string,
                    }),
                ),

            }),
        ),
        isSponsored: PropTypes.bool,
    }),
    isSingleLine: PropTypes.bool,
    isHovering: PropTypes.bool,
    onClick: PropTypes.func,
};

export default ProductItem;
export { ProductItemClick };
